import {SendCodeResponsePayload} from './../../../shared/models/api/sendCodeResponsePayload';
import {createSlice, PayloadAction, SliceCaseReducers} from '@reduxjs/toolkit';
import {
  GetHolderProfileCabinetResponsePayload,
  GetProfileSettingsResponsePayload,
  RequestResponsePayload,
  GetRequestResponsePayload,
  NotificationResponsePayload,
  UpdatingNotificationInfoRequestPayload,
  ECardInfoResponsePayload,
} from '../../../shared/models/api';
import {Photo} from '../../../shared/models/profile/photo';
import {ProvidingListApplication} from '../../../shared/models/api';

import {AppActions} from '../../actions';
import {Dictionary} from '../../../shared/dictionaries/model';
import {NotificationStatus} from '../../../shared/models/profile/notification';
import {capitalize, capitalizeAndAddDot} from '../../../shared/utils/text';
import {GetCardTrackingInfoResponsePayload} from '../../../types/getCardTrackingInfoResponsePayload';

export interface ProfileState {
  token: string | null;
  trackingInfo: GetCardTrackingInfoResponsePayload | null;
  pinCode: string | null;
  holder: GetHolderProfileCabinetResponsePayload | null;
  settings: GetProfileSettingsResponsePayload | null;
  photo: Photo | null;
  notifications: NotificationResponsePayload[] | null;
  notification: NotificationResponsePayload | null;
  applications: ProvidingListApplication[] | null;
  currentRequest: GetRequestResponsePayload | null;
  requests: RequestResponsePayload[] | null;
  eCardInfo: ECardInfoResponsePayload | null;

  changeContacts: {
    newValue: string | null;
    sendCodeResponse: SendCodeResponsePayload | null;
  };

  dictionaries: {
    typeApplicationDictionary: Dictionary;
    statusDictionary: Dictionary;
  };
  isSessionExpired: boolean;
  isAuthenticated: boolean;
}

const initialState: ProfileState = {
  token: null,
  trackingInfo: null,
  pinCode: null,
  holder: null,
  settings: null,
  photo: null,
  notifications: null,
  notification: null,
  applications: null,
  currentRequest: null,
  requests: null,
  eCardInfo: null,
  changeContacts: {
    newValue: null,
    sendCodeResponse: null,
  },
  dictionaries: {
    typeApplicationDictionary: {},
    statusDictionary: {},
  },
  isSessionExpired: false,
  isAuthenticated: false,
};

export const profileSlice = createSlice<ProfileState, SliceCaseReducers<ProfileState>>({
  name: '@@eskso/profile',
  initialState,
  reducers: {
    setHolderProfile: (state, action: PayloadAction<GetHolderProfileCabinetResponsePayload>) => {
      state.holder = action.payload;
      if (state.holder)
        state.holder.displayName = `${capitalize(action.payload?.firstName)} ${capitalize(
          action.payload?.middleName,
        )} ${capitalizeAndAddDot(action.payload?.lastName)}`;
    },
    setProfileSettings: (state, action: PayloadAction<GetProfileSettingsResponsePayload>) => {
      state.settings = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setTrackingInfo: (state, action: PayloadAction<GetCardTrackingInfoResponsePayload>) => {
      state.trackingInfo = action.payload;
    },
    setPinCode: (state, action: PayloadAction<string | null>) => {
      state.pinCode = action.payload;
    },
    setPhoto: (state, action: PayloadAction<Photo>) => {
      state.photo = action.payload;
    },
    setNotifications: (state, action: PayloadAction<any[]>) => {
      if (action.payload) {
        state.notifications = action.payload.slice().sort((a, b) => (b?.id || 0) - (a?.id || 0));
      }
    },
    setNotification: (state, action: PayloadAction<NotificationResponsePayload>) => {
      state.notification = action.payload;
    },
    updateNotificationStatus: (state, action: PayloadAction<UpdatingNotificationInfoRequestPayload>) => {
      if (!action.payload) {
        return;
      }

      state.notifications =
        state.notifications?.map((item) => {
          if (item.id === action.payload.notificationId) {
            return {
              ...item,
              statusId: action.payload.statusId,
              statusName: action.payload.statusId === NotificationStatus.NotRead ? 'Не прочитано' : 'Прочитано',
            };
          }

          return item;
        }) || [];
    },
    setApplications: (state, action: PayloadAction<ProvidingListApplication[]>) => {
      state.applications = action.payload;
      state.dictionaries.typeApplicationDictionary = action.payload?.reduce(
        (prev, current) => ({...prev, [current.typeApplicationId || '']: current.typeApplicationName}),
        {} as any,
      );
      state.dictionaries.statusDictionary = action.payload?.reduce(
        (prev, current) => ({...prev, [current.statusId || '']: current.statusName}),
        {} as any,
      );
    },
    setCurrentRequest: (state, action: PayloadAction<GetRequestResponsePayload>) => {
      state.currentRequest = action.payload;
    },
    setRequests: (state, action: PayloadAction<RequestResponsePayload[]>) => {
      state.requests = action.payload;
    },
    setSessionState: (state, action: PayloadAction<boolean>) => {
      state.isSessionExpired = action.payload;
    },
    setAuthenticationState: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setECardInfo: (state, action: PayloadAction<ECardInfoResponsePayload>) => {
      state.eCardInfo = action.payload;
    },
    setChangeContactsNewValue: (state, action: PayloadAction<string>) => {
      state.changeContacts.newValue = action.payload;
    },
    setChangeContactsResponse: (state, action: PayloadAction<SendCodeResponsePayload>) => {
      state.changeContacts.sendCodeResponse = action.payload;
    },
    setHasPassword: (state, action: PayloadAction<boolean>) => {
      if (state.holder) {
        state.holder.hasPassword = action.payload;
      }
    },
    signOutDobroRu: (state) => {
      if (state.holder) state.holder = {...state.holder, volunteerId: undefined};
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: {
    [AppActions.Reset]: (state) => {
      const {token, pinCode, isAuthenticated} = state;
      return {
        ...initialState,
        isAuthenticated,
        token,
        pinCode,
      };
    },
  },
});

export const profile = profileSlice.reducer;
